import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProcessingQueue = ({ files, onRemoveFile, onRetryFile }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return { name: 'Upload', color: 'var(--color-accent)', className: 'animate-pulse' };
      case 'processing':
        return { name: 'Loader', color: 'var(--color-primary)', className: 'animate-spin' };
      case 'completed':
        return { name: 'CheckCircle', color: 'var(--color-success)' };
      case 'failed':
        return { name: 'AlertCircle', color: 'var(--color-error)' };
      default:
        return { name: 'Clock', color: 'var(--color-muted-foreground)' };
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'AI Analysis in Progress';
      case 'completed':
        return 'Analysis Complete';
      case 'failed':
        return 'Processing Failed';
      default:
        return 'Queued';
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'uploading':
        return 'bg-accent';
      case 'processing':
        return 'bg-primary';
      case 'completed':
        return 'bg-success';
      case 'failed':
        return 'bg-error';
      default:
        return 'bg-muted';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  if (files?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="FileText" size={24} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No files uploaded yet</h3>
        <p className="text-muted-foreground">Upload CV files to start AI processing</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Processing Queue</h3>
        <span className="text-sm text-muted-foreground">
          {files?.length} file{files?.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="space-y-3">
        {files?.map((file) => {
          const statusIcon = getStatusIcon(file?.status);
          
          return (
            <div 
              key={file?.id}
              className="bg-card border border-border rounded-lg p-4 card-elevation-base"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 mt-1">
                    <Icon 
                      name={statusIcon?.name} 
                      size={20} 
                      color={statusIcon?.color}
                      className={statusIcon?.className}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground truncate">
                      {file?.name}
                    </h4>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-sm text-muted-foreground">
                        {formatFileSize(file?.size)}
                      </span>
                      <span className={`text-sm font-medium ${
                        file?.status === 'completed' ? 'text-success' :
                        file?.status === 'failed' ? 'text-error' :
                        file?.status === 'processing'? 'text-primary' : 'text-muted-foreground'
                      }`}>
                        {getStatusText(file?.status)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {file?.status === 'failed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RotateCcw"
                      onClick={() => onRetryFile(file?.id)}
                    >
                      Retry
                    </Button>
                  )}
                  
                  {file?.status !== 'processing' && file?.status !== 'uploading' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => onRemoveFile(file?.id)}
                    />
                  )}
                </div>
              </div>
              {/* Progress Bar */}
              <div className="w-full bg-muted rounded-full h-2 mb-3">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(file?.status)}`}
                  style={{ width: `${file?.progress}%` }}
                />
              </div>
              {/* AI Score and Actions */}
              {file?.status === 'completed' && file?.aiScore && (
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Brain" size={16} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground">
                        AI Score: {file?.aiScore}/100
                      </span>
                    </div>
                    
                    {file?.candidateName && (
                      <div className="flex items-center space-x-2">
                        <Icon name="User" size={16} color="var(--color-muted-foreground)" />
                        <span className="text-sm text-muted-foreground">
                          {file?.candidateName}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm">
                      View Details
                    </Button>
                    <Button variant="default" size="sm">
                      Add to Pipeline
                    </Button>
                  </div>
                </div>
              )}
              {/* Error Message */}
              {file?.status === 'failed' && file?.error && (
                <div className="flex items-start space-x-2 pt-3 border-t border-border">
                  <Icon name="AlertTriangle" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-error">{file?.error}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProcessingQueue;